import React from "react"
import { AttachmentType, ShareAttachmentFragment } from "~/__generated__/graphql"
import { MessageAttachmentPoll } from "./message-attachment-poll"
import { MessageAttachmentQAndA } from "./message-attachment-qa"
import { MessageAttachmentAudio } from "./message-attachment-audio"
import { MessageAttachmentVideo } from "./message-attachment-video"
import { MessageAttachmentTrivia } from "./message-attachment-trivia"
import { UnsupportedAttachment } from "./unsupported-attachment"

// Append Imgix auto=format to convert browser-incompatible formats (e.g. HEIC)
// to supported formats like WebP/JPEG based on the browser's Accept header.
// https://linear.app/tanookilabs/issue/CBC-2254
function withAutoFormat(imageUrl: string | null | undefined): string {
  if (!imageUrl) return ""
  try {
    const url = new URL(imageUrl)
    url.searchParams.set("auto", "format")
    return url.toString()
  } catch {
    return imageUrl
  }
}

interface MessageAttachmentsProps {
  attachments: ShareAttachmentFragment[] | null | undefined
  className?: string
}

export const MessageAttachments: React.FC<MessageAttachmentsProps> = ({
  attachments,
  className,
}) => {
  if (!attachments || attachments.length === 0) return null

  const attachment = attachments[0]

  switch (attachment.attachmentType) {
    case AttachmentType.Image:
      return (
        <img
          src={withAutoFormat(attachment.imageUrl)}
          alt="attachment"
          className={`max-h-[400px] w-full max-w-[400px] rounded-md object-cover ${className}`}
        />
      )
    case AttachmentType.Giphy:
      return (
        <img
          src={attachment.giphy?.data?.images?.original_still?.url}
          alt="attachment"
          className={`max-h-[250px] w-full max-w-[400px] rounded-md object-cover ${className}`}
        />
      )
    case AttachmentType.Poll:
      return attachment.poll ? <MessageAttachmentPoll poll={attachment.poll} /> : null
    case AttachmentType.QAndA:
      return (
        <MessageAttachmentQAndA
          qAndAColor={attachment.qAndAColor}
          qAndAPrompt={attachment.qAndAPrompt}
        />
      )
    case AttachmentType.Audio:
      return <MessageAttachmentAudio />
    case AttachmentType.Video:
      return (
        <MessageAttachmentVideo
          muxPlaybackId={attachment.muxPlaybackId}
          className={`max-h-[250px] w-full max-w-[400px] rounded-md object-cover ${className}`}
        />
      )
    case AttachmentType.Trivia:
      if (!attachment.trivia) return null

      return (
        <MessageAttachmentTrivia
          title={attachment.trivia.title}
          color={attachment.trivia.color}
          responseCount={attachment.trivia.responseCount}
        />
      )
    default:
      return <UnsupportedAttachment />
  }
}
